import { v4 as uuidv4 } from 'uuid'
import { buildBody, splitEntry } from './activity-batcher'
import { isExpired, type ActivityOutboxStore, type UnloadJournal } from './activity-outbox'
import type { ActivityClock } from './activity-clock'
import {
  ACTIVITY_500_MAX_ATTEMPTS, ACTIVITY_KEEPALIVE_MAX_BYTES, ACTIVITY_KEEPALIVE_WAIT_MS, type ActivityResponse, type ActivityViewport,
  type OutboxEntry,
} from './activity.model'

export interface ActivityToken { token: string; userId: string; expMs: number }

export interface ActivitySenderDeps {
  url: string
  appVersion: string
  fetchFn: typeof fetch
  clock: ActivityClock
  /** Resolves a token for the signed-in user; `force` refreshes first. Null = none available. */
  getToken: (force: boolean) => Promise<ActivityToken | null>
  viewport: () => ActivityViewport
  dev: boolean
  random?: () => number
  newId?: () => string
  /** How long a drain waits for an in-flight keepalive's outcome (default `ACTIVITY_KEEPALIVE_WAIT_MS`). */
  keepaliveWaitMs?: number
}

export interface ActivitySenderHooks {
  /** A 409 SESSION_OWNER_MISMATCH for this session. */
  onOwnerMismatch(sessionId: string): void
  /** A 403: the user has no right to post activity; stop tracking for them. */
  onForbidden(userId: string): void
}

type Outcome =
  | { kind: 'sent' } | { kind: 'drop' } | { kind: 'keep' } | { kind: 'split' }
  | { kind: 'mismatch' } | { kind: 'forbidden' }

const BACKOFF_MAX_MS = 5 * 60_000
const KEEP_STATUSES = new Set([404, 408, 429])
/** Statuses with their own handling in `sendOne` (or kept with backoff): never a plain drop. */
const NON_DROP_4XX = new Set([401, 403, 404, 408, 409, 413, 429])
const KEEPALIVE_SETTLED_MAX = 50

/** A 400, or any 4xx without special handling: the batch is refused for good and dropped. */
export function isPlainDrop(status: number): boolean {
  return status === 400 || (status >= 400 && status < 500 && !NON_DROP_4XX.has(status))
}

/** Backoff for the next attempt: min(5 min, 5 s x 2^attempts) x (0.8..1.2). */
export function backoffMs(attempts: number, random: () => number = Math.random): number {
  return Math.min(BACKOFF_MAX_MS, 5_000 * 2 ** Math.min(attempts, 20)) * (0.8 + random() * 0.4)
}

/**
 * Sends outbox entries to `POST /activity/events` with raw `fetch` (it must bypass
 * the HTTP interceptors: no self-counting; HttpClient cannot do keepalive). One
 * drain at a time. Pure of Angular; IO injected.
 */
export class ActivitySender {
  private halted = false
  /** A drain was requested while one was running: it re-runs once, so a fresh entry is never left waiting for a timer. */
  private drainAgain = false
  private cached: ActivityToken | null = null
  /** The keepalive in flight; `settled` never rejects. */
  private keepalive: { id: string; settled: Promise<void> } | null = null
  /** Ids a keepalive delivered or got dropped (insertion order, capped): the drain deletes such a row unsent. */
  private readonly keepaliveSettled = new Set<string>()
  private readonly warned = new Set<string>()

  constructor(
    private readonly deps: ActivitySenderDeps,
    private readonly store: ActivityOutboxStore,
    private readonly journal: UnloadJournal,
    private readonly hooks: ActivitySenderHooks,
  ) {}

  private warnOnce(key: string, msg: string, level: 'warn' | 'error' = 'warn'): void {
    if (this.warned.has(key)) return
    this.warned.add(key)
    console[level](`activity: ${msg}`)
  }

  private newId(): string { return (this.deps.newId ?? uuidv4)() }

  /** The cached token if still valid for > 60 s (the unload path cannot await). */
  cachedToken(): ActivityToken | null {
    const c = this.cached
    return c && c.expMs - this.deps.clock.wall() > 60_000 ? c : null
  }

  private async token(force: boolean): Promise<ActivityToken | null> {
    const t = await this.deps.getToken(force).catch(() => null)
    if (t) this.cached = t
    return t
  }

  /** Warms the token cache (so the hidden/close path can send synchronously). */
  async warmToken(): Promise<void> { await this.token(false) }

  get isHalted(): boolean { return this.halted }

  get isDraining(): boolean { return this.running !== null }

  /** Sign-out / lost session: from now on nothing is written to the store or the journal, whatever is still in flight. */
  halt(): void { this.halted = true }

  private async put(e: OutboxEntry): Promise<void> { if (!this.halted) await this.store.put(e) }
  private async del(id: string): Promise<void> { if (!this.halted) await this.store.delete(id) }

  private running: Promise<void> | null = null

  /**
   * Drains the due entries of `userId`, oldest first. Resolves when nothing is due. A call while
   * one runs returns that run's promise (and makes it go round once more), so a caller that
   * awaits it — sign-out — really waits.
   */
  drain(userId: string): Promise<void> {
    if (this.running) { this.drainAgain = true; return this.running }
    const run = (async () => {
      do {
        this.drainAgain = false
        await this.drainOnce(userId)
      } while (this.drainAgain && !this.halted)
    })().finally(() => { this.running = null })
    this.running = run
    return run
  }

  /**
   * Waits (at most `keepaliveWaitMs`, a timer, never a wall-clock comparison) for the in-flight
   * keepalive's outcome, so an entry it delivered is not sent again. On a timeout the request is
   * abandoned for bookkeeping: a later hide may start a new keepalive.
   */
  private async awaitKeepalive(): Promise<void> {
    const ka = this.keepalive
    if (!ka) return
    let timer: ReturnType<typeof setTimeout> | undefined
    const timeout = new Promise<'timeout'>(resolve => {
      timer = setTimeout(() => resolve('timeout'), this.deps.keepaliveWaitMs ?? ACTIVITY_KEEPALIVE_WAIT_MS)
    })
    try {
      const r = await Promise.race([ka.settled.then(() => 'settled' as const), timeout])
      if (r === 'timeout' && this.keepalive === ka) this.keepalive = null
    } finally {
      clearTimeout(timer)
    }
  }

  private async drainOnce(userId: string): Promise<void> {
    await this.awaitKeepalive()
    if (this.halted) return
    let tok = await this.token(false)
    if (!tok || tok.userId !== userId || this.halted) return
    const queue = (await this.store.getAll())
      .filter(e => e.userId === userId)
      .sort((a, b) => a.createdAtWall - b.createdAtWall)
    for (let i = 0; i < queue.length; i++) {
      if (this.halted) return
      const entry = queue[i]
      if (this.keepaliveSettled.has(entry.id)) { await this.del(entry.id); continue }
      const now = this.deps.clock.wall()
      if (isExpired(entry, now)) { await this.del(entry.id); continue }
      if (entry.nextAttemptAt > now) continue
      tok = (await this.token(false)) ?? tok
      // a token refresh may have switched the signed-in user: never send one user's entry under another's token
      if (tok.userId !== userId || tok.userId !== entry.userId) return
      const outcome = await this.sendOne(entry, tok, true)
      if (this.halted) return
      if (outcome.kind === 'split') {
        const halves = splitEntry(entry, () => this.newId())
        if (halves) {
          await this.del(entry.id)
          for (const h of halves) await this.put(h)
          queue.splice(i + 1, 0, ...halves) // the loop reaches both halves next
        } else {
          console.warn('activity: a single item exceeds the body limit; dropped')
          await this.del(entry.id)
        }
      }
      if (outcome.kind === 'forbidden') return
    }
  }

  private async post(entry: OutboxEntry, token: string, keepalive: boolean): Promise<Response> {
    const body = buildBody(entry, this.deps.appVersion, this.deps.viewport(), this.deps.clock.wall())
    return this.deps.fetchFn(this.deps.url, {
      method: 'POST', mode: 'cors', credentials: 'omit', keepalive,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body,
    })
  }

  private async sendOne(entry: OutboxEntry, tok: ActivityToken, canRefresh: boolean): Promise<Outcome> {
    let res: Response
    try {
      res = await this.post(entry, tok.token, false)
    } catch {
      return this.backoff(entry, false)
    }
    const status = res.status
    if (status === 200) {
      if (this.deps.dev) await this.logRejections(res)
      await this.del(entry.id)
      if (!this.halted) this.journal.remove(entry)
      return { kind: 'sent' }
    }
    if (status === 401) {
      if (canRefresh) {
        const fresh = await this.token(true)
        if (fresh && fresh.userId === entry.userId) return this.sendOne(entry, fresh, false)
      }
      return this.backoff(entry, false)
    }
    if (status === 403) {
      await this.del(entry.id)
      this.hooks.onForbidden(entry.userId)
      return { kind: 'forbidden' }
    }
    if (status === 413) return { kind: 'split' }
    if (status === 409) {
      const code = await res.json().then((b: { code?: string }) => b?.code, () => undefined)
      await this.del(entry.id)
      if (code === 'SESSION_OWNER_MISMATCH') { this.hooks.onOwnerMismatch(entry.sessionId); return { kind: 'mismatch' } }
      this.warnOnce('409', 'unexpected 409 from /activity/events; batch dropped')
      return { kind: 'drop' }
    }
    if (isPlainDrop(status)) {
      this.warnDrop(status)
      await this.del(entry.id)
      return { kind: 'drop' }
    }
    if (status === 500) return this.backoff(entry, true)
    if (KEEP_STATUSES.has(status) || status >= 500) return this.backoff(entry, false)
    return this.backoff(entry, false)
  }

  private warnDrop(status: number): void {
    if (status === 400) this.warnOnce('400', 'the server refused a batch as invalid (client bug); batch dropped', 'error')
    else this.warnOnce(`s${status}`, `unexpected ${status} from /activity/events; batch dropped`)
  }

  /** Keep with exponential backoff; `capped` entries (500) are deleted after 10 attempts. */
  private async backoff(entry: OutboxEntry, capped: boolean): Promise<Outcome> {
    const attempts = entry.attempts + 1
    if (capped && attempts >= ACTIVITY_500_MAX_ATTEMPTS) {
      console.warn(`activity: batch dropped after ${attempts} failed attempts (server error)`)
      await this.del(entry.id)
      return { kind: 'drop' }
    }
    const next: OutboxEntry = { ...entry, attempts, nextAttemptAt: this.deps.clock.wall() + backoffMs(entry.attempts, this.deps.random) }
    Object.assign(entry, next)
    await this.put(next)
    return { kind: 'keep' }
  }

  private async logRejections(res: Response): Promise<void> {
    try {
      const body = await res.json() as ActivityResponse
      for (const r of body.rejections ?? []) this.warnOnce(`rej:${r.kind}:${r.reason}`, `server rejected a ${r.kind}: ${r.reason}`)
    } catch { /* body is optional */ }
  }

  /** Earliest `nextAttemptAt` of the user's stored entries, or null when none (drives the 60-s drain timer). */
  async hasDue(userId: string): Promise<boolean> {
    const now = this.deps.clock.wall()
    return (await this.store.getAll()).some(e => e.userId === userId && e.nextAttemptAt <= now)
  }

  private rememberSettled(id: string): void {
    this.keepaliveSettled.add(id)
    if (this.keepaliveSettled.size > KEEPALIVE_SETTLED_MAX) {
      this.keepaliveSettled.delete(this.keepaliveSettled.values().next().value as string)
    }
  }

  /**
   * A keepalive's answer, page alive. A 200 (parity with `sendOne`) or a plain drop settles the
   * entry: gone from the journal and, if migration already moved it, from the outbox. Anything
   * else (hooks-worthy statuses, 5xx) leaves it for `sendOne`, which alone owns the hooks, token
   * refresh, split and backoff. `logRejections` is not run here (dev only): the same items'
   * rejections are logged if they are ever sent normally. Never rejects.
   */
  private async onKeepaliveResponse(entry: OutboxEntry, res: Response): Promise<void> {
    try {
      if (this.halted) return
      const status = res.status
      if (status !== 200 && !isPlainDrop(status)) return
      this.rememberSettled(entry.id)
      this.journal.remove(entry)
      if (status !== 200) this.warnDrop(status)
      await this.del(entry.id).catch(() => undefined)
    } catch { /* the entry stays; sendOne replays it */ }
  }

  /**
   * The unload send: synchronous up to `fetch` (no await before it), keepalive,
   * only with a still-valid cached token, one request in flight, <= 60 KB. Never
   * touches IndexedDB while sending; once the answer arrives (page alive) a settled
   * entry is also deleted from the outbox if migration already moved it there. The
   * caller has already journalled the entry; a drain waits up to `keepaliveWaitMs`
   * for the outcome instead of resending the entry.
   */
  sendKeepalive(entry: OutboxEntry): void {
    const tok = this.cachedToken()
    if (!tok || tok.userId !== entry.userId || this.keepalive) return
    if (entry.bytes + 512 > ACTIVITY_KEEPALIVE_MAX_BYTES) return
    let request: Promise<Response>
    try {
      request = this.post(entry, tok.token, true)
    } catch {
      return
    }
    const ka: { id: string; settled: Promise<void> } = {
      id: entry.id,
      settled: request
        .then(res => this.onKeepaliveResponse(entry, res), () => { /* the journal keeps it */ })
        .finally(() => { if (this.keepalive === ka) this.keepalive = null }),
    }
    this.keepalive = ka
  }
}
